// Keyboard navigation for power users
import { stories, currentCategory, expandedStoryId, setExpandedStoryId } from './state.js';
import { toggleStory, renderStories } from './stories.js';
import { toggleBookmark } from './bookmarks.js';
import { showToast } from './utils.js';

let keyboardEnabled = true;
let selectedIndex = -1;
let helpModalOpen = false;

const SHORTCUTS = {
  'j': { action: 'next', desc: 'Next story' },
  'k': { action: 'prev', desc: 'Previous story' },
  'o': { action: 'open', desc: 'Open/close story' },
  'Enter': { action: 'open', desc: 'Open/close story' },
  'l': { action: 'like', desc: 'Like story' },
  'd': { action: 'dislike', desc: 'Dislike story' },
  'b': { action: 'bookmark', desc: 'Bookmark story' },
  's': { action: 'share', desc: 'Share story' },
  'x': { action: 'block', desc: 'Block menu' },
  '/': { action: 'search', desc: 'Focus search' },
  '?': { action: 'help', desc: 'Show shortcuts' },
  'Escape': { action: 'close', desc: 'Close modals' },
  'g h': { action: 'home', desc: 'Go to first category' },
  'g e': { action: 'end', desc: 'Go to last story' },
  '1': { action: 'goto1', desc: 'Jump to story 1' },
  '2': { action: 'goto2', desc: 'Jump to story 2' },
  '3': { action: 'goto3', desc: 'Jump to story 3' },
  '4': { action: 'goto4', desc: 'Jump to story 4' },
  '5': { action: 'goto5', desc: 'Jump to story 5' },
  '6': { action: 'goto6', desc: 'Jump to story 6' },
  '7': { action: 'goto7', desc: 'Jump to story 7' },
  '8': { action: 'goto8', desc: 'Jump to story 8' },
  '9': { action: 'goto9', desc: 'Jump to story 9' },
};

export function initKeyboardNavigation() {
  document.addEventListener('keydown', handleKeydown);
  
  // Add keyboard indicator to UI
  const indicator = document.createElement('div');
  indicator.id = 'keyboard-indicator';
  indicator.innerHTML = '<span class="kbd">?</span> Keyboard shortcuts';
  indicator.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: var(--bg-secondary);
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
    opacity: 0.7;
    cursor: pointer;
    z-index: 100;
    display: none;
  `;
  indicator.onclick = () => showHelpModal();
  document.body.appendChild(indicator);
  
  // Show indicator after first keyboard use
  document.addEventListener('keydown', () => {
    indicator.style.display = 'block';
  }, { once: true });
}

function handleKeydown(e) {
  // Ignore when typing in inputs
  if (e.target.matches('input, textarea, select')) return;
  if (!keyboardEnabled) return;
  
  const key = e.key;
  const shortcut = SHORTCUTS[key];
  
  if (!shortcut) return;
  
  e.preventDefault();
  executeAction(shortcut.action);
}

function executeAction(action) {
  const storyCards = document.querySelectorAll('.story-card');
  
  switch (action) {
    case 'next':
      selectedIndex = Math.min(selectedIndex + 1, stories.length - 1);
      highlightStory(selectedIndex);
      break;
      
    case 'prev':
      selectedIndex = Math.max(selectedIndex - 1, 0);
      highlightStory(selectedIndex);
      break;
      
    case 'open':
      if (selectedIndex >= 0 && stories[selectedIndex]) {
        toggleStory(stories[selectedIndex].id);
      }
      break;
      
    case 'like':
      if (selectedIndex >= 0 && stories[selectedIndex]) {
        window.appHandleFeedback(stories[selectedIndex].id, 'like');
        showToast('Liked! More like this');
      }
      break;
      
    case 'dislike':
      if (selectedIndex >= 0 && stories[selectedIndex]) {
        window.appHandleFeedback(stories[selectedIndex].id, 'dislike');
        showToast('Got it, less like this');
      }
      break;
      
    case 'bookmark':
      if (selectedIndex >= 0 && stories[selectedIndex]) {
        toggleBookmark(stories[selectedIndex].id);
      }
      break;
      
    case 'share':
      if (selectedIndex >= 0 && stories[selectedIndex]) {
        window.appShareStory(stories[selectedIndex].id);
      }
      break;
      
    case 'block':
      if (selectedIndex >= 0 && stories[selectedIndex]) {
        window.appOpenBlockModal(stories[selectedIndex].id);
      }
      break;
      
    case 'search':
      // Future: focus search input
      showToast('Search coming soon');
      break;
      
    case 'help':
      showHelpModal();
      break;
      
    case 'close':
      closeAllModals();
      break;
      
    case 'home':
      selectedIndex = 0;
      highlightStory(0);
      break;
      
    case 'end':
      selectedIndex = stories.length - 1;
      highlightStory(selectedIndex);
      break;
      
    default:
      // Handle number keys 1-9
      if (action.startsWith('goto')) {
        const num = parseInt(action.replace('goto', '')) - 1;
        if (num < stories.length) {
          selectedIndex = num;
          highlightStory(num);
          toggleStory(stories[num].id);
        }
      }
  }
}

function highlightStory(index) {
  // Remove previous highlight
  document.querySelectorAll('.story-card').forEach(card => {
    card.classList.remove('keyboard-selected');
  });
  
  // Add new highlight
  const cards = document.querySelectorAll('.story-card');
  if (cards[index]) {
    cards[index].classList.add('keyboard-selected');
    cards[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function showHelpModal() {
  if (helpModalOpen) return;
  helpModalOpen = true;
  
  const modal = document.createElement('div');
  modal.className = 'modal active';
  modal.id = 'keyboard-help-modal';
  
  const shortcutsList = Object.entries(SHORTCUTS)
    .filter(([key]) => !key.match(/^\d$/)) // Exclude number keys from main list
    .map(([key, { desc }]) => `
      <div class="shortcut-row">
        <span class="kbd">${key}</span>
        <span>${desc}</span>
      </div>
    `).join('');
  
  modal.innerHTML = `
    <div class="modal-content" style="max-width: 400px;">
      <div class="modal-header">
        <h2>⌨️ Keyboard Shortcuts</h2>
        <button class="modal-close" aria-label="Close">×</button>
      </div>
      <div class="modal-body">
        <div class="shortcuts-grid">
          ${shortcutsList}
          <div class="shortcut-row">
            <span class="kbd">1</span>-<span class="kbd">9</span>
            <span>Jump to story N</span>
          </div>
        </div>
      </div>
    </div>
  `;
  
  const closeModal = () => {
    modal.remove();
    helpModalOpen = false;
  };
  
  modal.querySelector('.modal-close').onclick = closeModal;
  modal.onclick = (e) => { if (e.target === modal) closeModal(); };
  
  document.body.appendChild(modal);
}

function closeAllModals() {
  document.querySelectorAll('.modal').forEach(m => m.remove());
  helpModalOpen = false;
}

// Reset selection when stories change
export function resetKeyboardSelection() {
  selectedIndex = -1;
  document.querySelectorAll('.story-card').forEach(card => {
    card.classList.remove('keyboard-selected');
  });
}

export { selectedIndex };
